import { Box, HStack, Progress, Table, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { formatLean } from '../analyticsMap/choropleth'
import { explainCopy } from '../../lib/explainCopy'
import type { ContestResultRow, DropoffPoint, TurnoutPoint } from '../../types/geoAnalytics'
import ProfileCard from './ProfileCard'
import { vizDiv } from '../shared/viz'

interface PoliticalBaselineCardProps {
  results: ContestResultRow[]
  turnout: TurnoutPoint[]
  dropoff: DropoffPoint[]
}

/** D-margin/R-margin/even — diverging by sign, mirrors the app's partisan idiom. */
function marginColor(marginPp: number): string {
  if (Math.abs(Math.round(marginPp * 10) / 10) < 0.05) return 'var(--chakra-colors-muted)'
  return marginPp > 0 ? vizDiv.pos : vizDiv.neg
}

export function PoliticalBaselineCard({ results, turnout, dropoff }: PoliticalBaselineCardProps) {
  // Latest usable drop-off point — the payload value is a RATE (% of presidential
  // voters who skipped the target election), not a fabricated head-count.
  const latestDropoff = [...dropoff]
    .filter((d) => !d.suppressed && d.value != null)
    .sort((a, b) => b.targetYear - a.targetYear)[0]

  return (
    <ProfileCard
      eyebrow="Political baseline"
      title="Recent results & turnout"
      source="Results: MEDSL precinct returns · Turnout: state voter file (VotingHistories)"
    >
      {results.length === 0 ? (
        <Text fontSize="sm" color="gray.500" mt={3}>
          No election results ingested for this geography.
        </Text>
      ) : (
        <>
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Recent election results
            </Text>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Precinct returns — loaded per contest
            </Text>
          </Box>
          <Box overflowX="auto" mt={3} mb={3}>
            <Table.Root size="sm">
              <Table.Header bg="gray.50">
                <Table.Row>
                  <Table.ColumnHeader>Contest</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Dem</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Rep</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Margin</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {results.map((r) => (
                  <Table.Row key={`${r.office}-${r.year}`}>
                    <Table.Cell>{`${r.office} ${r.year}`}</Table.Cell>
                    <Table.Cell textAlign='end'>{`${r.demSharePct.toFixed(1)}%`}</Table.Cell>
                    <Table.Cell textAlign='end'>{`${r.repSharePct.toFixed(1)}%`}</Table.Cell>
                    <Table.Cell color={marginColor(r.marginPp)} fontWeight="bold" textAlign='end'>
                      {formatLean(r.marginPp)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Results appear here only for contests with precinct-level returns loaded.
          </Text>
        </>
      )}

      {turnout.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">
            General-election turnout
          </Text>
          <Text fontSize="xs" color="gray.500" mb={1}>
            State voter file — every recent cycle
          </Text>
          {turnout.map((t) => {
            const label = `General ${t.year}`
            const valueText = t.suppressed
              ? 'insufficient data'
              : t.value != null
                ? `${t.value.toFixed(1)}%`
                : '—'
            return (
              <HStack key={t.year} gap={3} mb={1.5}>
                <Text fontSize="sm" w="110px" flexShrink={0} color="gray.700">
                  {label}
                </Text>
                <Progress.Root
                  flex="1"
                  value={t.value ?? 0}
                  max={100}
                  size="sm"
                  colorPalette="brand"
                  borderRadius="sm"
                  aria-label={`${label} turnout`}>
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
                <Text fontSize="sm" w="70px" flexShrink={0} textAlign="right" fontWeight="medium">
                  {valueText}
                </Text>
              </HStack>
            );
          })}
        </Box>
      )}

      {latestDropoff && (
        <Text fontSize="sm" mt={3}>
          <Tooltip
            content={explainCopy.dropoff(
              latestDropoff.value!,
              latestDropoff.targetYear - 2,
              String(latestDropoff.targetYear),
            )}
            showArrow
          >
            <Text as="span" fontWeight="bold" borderBottomWidth="1px" borderStyle="dotted" cursor="help">
              {`${Math.round(latestDropoff.value!)}% of presidential voters skipped ${latestDropoff.targetYear}`}
            </Text>
          </Tooltip>{' '}
          — the mobilize universe for lower-turnout races.
        </Text>
      )}
    </ProfileCard>
  );
}
